// The tree-level selector — the Google analog of the Meta breakdown's group-by
// chips, same placement (immediately above the table) and same behaviour.
//
// The user turns levels on and off and the tree nests over only the active ones,
// in canonical order. At least one level always stays on: the last active chip is
// disabled rather than silently refusing the click, so the rule is visible instead
// of feeling like a bug.

import { cn } from '../../lib/utils';
import {
  GOOGLE_ADS_LEVELS,
  GOOGLE_ADS_LEVEL_LABEL,
  deepestLevel,
} from '../../lib/googleAdsLevels';
import type { GoogleAdsLevel } from '../../types/googleAdsReporting';

export function LevelChips({
  levels,
  onToggle,
  busy,
}: {
  levels: GoogleAdsLevel[];
  onToggle: (level: GoogleAdsLevel) => void;
  busy?: boolean;
}): React.JSX.Element {
  const deepest = deepestLevel(levels);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">group by</span>
      {GOOGLE_ADS_LEVELS.map((level) => {
        const active = levels.includes(level);
        const isLastActive = active && levels.length === 1;
        return (
          <button
            key={level}
            type="button"
            onClick={() => onToggle(level)}
            aria-pressed={active}
            disabled={isLastActive}
            title={
              isLastActive
                ? 'At least one level has to stay on'
                : active
                  ? `Stop grouping by ${GOOGLE_ADS_LEVEL_LABEL[level]}`
                  : `Group by ${GOOGLE_ADS_LEVEL_LABEL[level]}`
            }
            className={cn(
              'rounded-full border px-3 py-1 text-sm transition-colors',
              active
                ? 'border-kyck-600 bg-kyck-50 font-medium text-kyck-800'
                : 'border-input bg-background text-muted-foreground hover:bg-accent',
              isLastActive && 'cursor-not-allowed opacity-70',
            )}
          >
            {GOOGLE_ADS_LEVEL_LABEL[level]}
          </button>
        );
      })}
      <span className="text-xs text-muted-foreground">
        {/* The drill floor, stated — otherwise a row that stops being clickable
            after a chip toggle reads as a bug. */}
        drill stops at {GOOGLE_ADS_LEVEL_LABEL[deepest].toLowerCase()}
      </span>
      {busy && <span className="text-xs text-muted-foreground">updating…</span>}
    </div>
  );
}
